import { Collapse } from '@mantine/core';
import { IconAlertSquareRounded } from '@tabler/icons-react';
import { useState, useMemo, useEffect } from 'react';
import type { SelectedStep } from '~/server/logic/prisma/step-select';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import type { AiChange, conditionalBlockResult, TestRunExecutionOutput } from '~/types';
import type { OperationError, OperationLog } from './ExecutedOperationView';
import ExecutionStepHeader from './ExecutionStepHeader';
import ExecutionOperationDisplayList, { type TraceErroredOperation } from './ExecutionOperationDisplayList';

export type ExecutedOperationPair = {
  inputOperation?: SelectedOperation;
  outputOperation?: SelectedOperation;
  isAutoheal?: boolean;
};

export default function ExecutionStepViewer({
  outputStep,
  inputStep,
  stepIndex,
  aiChanges,
  inputOperationErrors,
  outputOperationErrors,
  conditionalBlockResults,
  allExpanded,
  operationErrorByOperationId,
  operationLogsByOperationId,
  selectOperationsForScreenshot,
  selectedOperations,
  testRunOutput,
}: {
  outputStep?: SelectedStep;
  inputStep?: SelectedStep;
  stepIndex: number;
  aiChanges?: AiChange[];
  inputOperationErrors?: OperationError[];
  outputOperationErrors?: OperationError[];
  conditionalBlockResults?: conditionalBlockResult[];
  allExpanded?: boolean;
  operationErrorByOperationId?: Partial<Record<string, OperationError>>;
  operationLogsByOperationId?: Partial<Record<string, OperationLog[]>>;
  selectOperationsForScreenshot?: (params: {
    originalOperationId?: string;
    newOperationId?: string;
    rightDirectScreenshotUrl?: string;
    rightDirectScreenshots?: { before?: string; action?: string; after?: string };
  }) => void;
  selectedOperations?: { originalOperationId?: string; newOperationId?: string };
  testRunOutput?: TestRunExecutionOutput;
}) {
  const hasStepErrors = !!inputOperationErrors?.length || !!outputOperationErrors?.length;
  const conditionalBlockResultsForStep = conditionalBlockResults ?? [];

  const [expanded, setExpanded] = useState(
    hasStepErrors || !!aiChanges?.length || conditionalBlockResultsForStep.length > 0,
  );

  useEffect(() => {
    if (allExpanded !== undefined) {
      setExpanded(allExpanded);
    }
  }, [allExpanded]);

  // Track whether this step was executed (has output)
  const wasExecuted = outputStep != null;

  const operationPairs = useMemo(() => {
    const aiChangesForStep = aiChanges?.filter((c) => c.erroredOperationId !== undefined) ?? [];
    const outputOperations = outputStep?.operations ?? [];
    const inputOperations = inputStep?.operations ?? [];

    // If there are no AI changes, we just want to display the operations in pairs
    if (aiChangesForStep.length === 0) {
      const maxOps = Math.max(inputOperations.length, outputOperations.length);
      return Array.from({ length: maxOps }, (_, i) => ({
        inputOperation: inputOperations[i],
        outputOperation: outputOperations[i],
      }));
    }

    const [firstAiChange] = aiChangesForStep;
    const firstAutohealOperationIdx =
      firstAiChange.firstAutohealOperationId === undefined ?
        outputOperations.length
      : outputOperations.findIndex((op) => op.id === firstAiChange.firstAutohealOperationId);

    const maxOps = Math.max(inputOperations.length, outputOperations.length);
    return Array.from({ length: maxOps }, (_, i) => ({
      inputOperation: inputOperations[i],
      outputOperation: outputOperations[i],
      // if the operation is after the first autoheal operation, it is an autoheal operation
      ...(i >= firstAutohealOperationIdx && { isAutoheal: true as const }),
    }));
  }, [outputStep, aiChanges, inputStep]);

  const erroredOperationIdsFromAiChanges = useMemo(
    () => [
      ...new Set(
        aiChanges?.map((change) => change.erroredOperationId).filter((id): id is string => id !== undefined) ?? [],
      ),
    ],
    [aiChanges],
  );

  const traceErrorsFromInput = useMemo(() => {
    if (!inputOperationErrors || inputOperationErrors.length === 0) {
      return [];
    }

    const aiErroredIdSet = new Set(erroredOperationIdsFromAiChanges);
    return inputOperationErrors.filter((error) => aiErroredIdSet.has(error.operationId));
  }, [inputOperationErrors, erroredOperationIdsFromAiChanges]);

  const traceErroredOperationIds = useMemo(() => {
    const ids = new Set<string>(erroredOperationIdsFromAiChanges);
    traceErrorsFromInput.forEach((error) => ids.add(error.operationId));
    return Array.from(ids);
  }, [erroredOperationIdsFromAiChanges, traceErrorsFromInput]);

  const traceErroredOperations = useMemo<TraceErroredOperation[]>(() => {
    // If step wasn't executed, there are no trace errors to show
    if (!outputStep) {
      return [];
    }

    // NEW: Try to use erroredAutoHealableOperations first (preferred)
    // Note: erroredAutoHealableOperations uses the original/input step ID, not the output step ID
    const erroredOpsFromOutput =
      testRunOutput?.erroredAutoHealableOperations?.filter((eo) => eo.stepId === (inputStep?.id ?? outputStep.id)) ??
      [];

    if (erroredOpsFromOutput.length > 0) {
      // Sort by operation order to display errors in execution sequence (maintains chronological flow for user clarity)
      const sortedErroredOps = [...erroredOpsFromOutput].sort((a, b) => a.order - b.order);

      return sortedErroredOps.flatMap((erroredOp): TraceErroredOperation[] => {
        const inputOperation = inputStep?.operations.find((op) => op.id === erroredOp.originalOperationId);
        const outputOperation = outputStep.operations.find((op) => op.id === erroredOp.originalOperationId);
        const operation = inputOperation ?? outputOperation;

        if (!operation) {
          console.warn('Could not find operation for erroredOperation:', erroredOp);
          return [];
        }

        const stepForOperation = inputOperation && inputStep ? inputStep : outputStep;

        // Look up logs using both input and output operation IDs since logs may be stored under either
        // (logs are typically stored under the output operation ID during execution)
        const inputOpIndex = inputStep?.operations.findIndex((op) => op.id === erroredOp.originalOperationId) ?? -1;
        const correspondingOutputOp = inputOpIndex >= 0 ? outputStep.operations[inputOpIndex] : undefined;
        const operationLogs =
          operationLogsByOperationId?.[operation.id] ??
          (correspondingOutputOp ? operationLogsByOperationId?.[correspondingOutputOp.id] : undefined) ??
          [];

        return [
          {
            operation,
            step: stepForOperation,
            operationError: {
              stepId: erroredOp.stepId,
              stepIndex,
              stepDescription: outputStep.description,
              operationId: erroredOp.originalOperationId,
              operationDescription: erroredOp.operationDescription,
              error: erroredOp.error,
            },
            operationLogs,
            aiChanges: (aiChanges ?? []).filter(
              (change) => change.erroredOperationId === erroredOp.originalOperationId,
            ),
            errorScreenshotUrl: erroredOp.screenshotUrl,
          },
        ];
      });
    }

    // FALLBACK (OLD LOGIC): For old test runs without erroredAutoHealableOperations
    // This ensures users can still view test runs created before this feature was deployed
    if (traceErrorsFromInput.length === 0) {
      return [];
    }

    return traceErrorsFromInput.flatMap((error) => {
      const inputOperation = inputStep?.operations.find((op) => op.id === error.operationId);
      const outputOperation = outputStep.operations.find((op) => op.id === error.operationId);
      const operation = inputOperation ?? outputOperation;

      if (!operation) {
        return [];
      }

      const stepForOperation = inputOperation && inputStep ? inputStep : outputStep;

      // Look up logs using both input and output operation IDs since logs may be stored under either
      const inputOpIndex = inputStep?.operations.findIndex((op) => op.id === error.operationId) ?? -1;
      const correspondingOutputOp = inputOpIndex >= 0 ? outputStep.operations[inputOpIndex] : undefined;
      const operationLogs =
        operationLogsByOperationId?.[operation.id] ??
        (correspondingOutputOp ? operationLogsByOperationId?.[correspondingOutputOp.id] : undefined) ??
        [];

      return [
        {
          operation,
          step: stepForOperation,
          operationError: operationErrorByOperationId?.[error.operationId] ?? error,
          operationLogs,
          aiChanges: (aiChanges ?? []).filter((change) => change.erroredOperationId === error.operationId),
          errorScreenshotUrl: undefined, // OLD: No screenshot URL, will show "no screenshot" indicator
        } satisfies TraceErroredOperation,
      ];
    });
  }, [
    testRunOutput,
    traceErrorsFromInput,
    inputStep,
    outputStep,
    stepIndex,
    aiChanges,
    operationErrorByOperationId,
    operationLogsByOperationId,
  ]);

  const stepHealSuccess = aiChanges?.every((c) => c.isSuccess);

  return (
    <div className="flex items-start">
      <div className="mr-4 w-3 pt-[10px] text-sm text-[var(--mantine-color-dimmed)]">{stepIndex + 1}</div>
      <div className="flex-1">
        <div className="overflow-hidden rounded-lg border border-[var(--mantine-color-default-border)] shadow-sm transition-colors hover:border-neutral-400 dark:hover:border-neutral-600">
          <div className="table w-full py-1">
            <div className="group table-row">
              <div className="table-cell w-1/2 border-r border-[var(--mantine-color-default-border)] align-top">
                {/* Show input step header, or fall back to output step for older runs without input content */}
                {(inputStep ?? outputStep) != null && (
                  <ExecutionStepHeader
                    step={(inputStep ?? outputStep)!}
                    expanded={expanded}
                    setExpanded={setExpanded}
                  />
                )}
              </div>
              <div className="table-cell w-1/2">
                {outputStep != null ?
                  <ExecutionStepHeader
                    step={outputStep}
                    expanded={expanded}
                    setExpanded={setExpanded}
                    aiChanges={aiChanges}
                  />
                : <div className="flex h-full items-center px-4 py-2 text-sm text-[var(--mantine-color-dimmed)]">
                    Not executed
                  </div>
                }
              </div>
            </div>
          </div>

          <Collapse in={expanded}>
            <div className="border-t border-[var(--mantine-color-default-border)]">
              {outputStep?.mode === 'AI_DYNAMIC' && (
                <div className="table w-full py-1">
                  <div className="table-row">
                    <div className="table-cell w-1/2 border-r border-[var(--mantine-color-default-border)] p-4" />
                    <div className="table-cell w-1/2 p-4">
                      <div className="space-y-2">
                        <div className="rounded py-2 pl-4 text-xs font-medium text-[var(--mantine-color-dimmed)]">
                          AI step - dynamic actions each time
                        </div>
                        <div className="rounded-md border border-orange-200 bg-orange-50 p-3 dark:border-orange-800 dark:bg-orange-900/20">
                          <div className="flex items-start space-x-2">
                            <IconAlertSquareRounded
                              size={16}
                              className="mt-0.5 flex-shrink-0 text-orange-600 dark:text-orange-400"
                            />
                            <div className="text-xs">
                              <div className="mb-1 font-medium text-orange-800 dark:text-orange-200">Deprecated</div>
                              <div className="text-orange-700 dark:text-orange-300">
                                If this step is truly dynamic, migrate to an <strong>&ldquo;AI Act&rdquo;</strong>{' '}
                                action. Search for &ldquo;AI Act&rdquo; in the editor.
                                <br />
                                <br />
                                If the content is not dynamic, migrate to <strong>&ldquo;Auto-heal&rdquo;</strong> step
                                mode by clicking the <strong>Save</strong> button for this step when editing the test.
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <ExecutionOperationDisplayList
                operationPairs={operationPairs}
                inputStep={inputStep}
                outputStep={outputStep}
                operationErrorByOperationId={operationErrorByOperationId}
                operationLogsByOperationId={operationLogsByOperationId}
                selectOperationsForScreenshot={selectOperationsForScreenshot}
                selectedOperations={selectedOperations}
                aiChanges={aiChanges}
                conditionalBlockResultsForStep={conditionalBlockResultsForStep}
                stepHealSuccess={stepHealSuccess}
                traceErroredOperations={traceErroredOperations}
                traceErroredOperationIds={traceErroredOperationIds}
                aiActionLogs={testRunOutput?.aiActionLogs ?? []}
                wasExecuted={wasExecuted}
              />
            </div>
          </Collapse>
        </div>
      </div>
    </div>
  );
}
